import { create } from "zustand";
import type { BoundaryOverlapHit } from "../types/contract";

// The add-field OVERLAP GATE: "when a new boundary is added, it should first check whether it
// overlaps with any existing boundaries for that organization.
export interface OverlapAsk {
  name: string; // what the user is bringing in
  candidate: unknown | null; // candidate geometry when we hold it client-side (draw / inline geojson)
  hits: BoundaryOverlapHit[];
  /*
   * true when the field already EXISTS (server-parsed shp/kml/kmz + JD) — declining must then remove it, vs the
   * pre-create case where declining simply aborts and nothing was ever written.
   */
  alreadyCreated: boolean;
}
type Entry = OverlapAsk & { id: number; resolve: (keep: boolean) => void };

interface State {
  queue: Entry[]; // [0] is the one on screen
  removalFailed: string | null;
  ask: (a: OverlapAsk) => Promise<boolean>;
  answer: (keep: boolean) => void;
  /*
   * Resolve EVERY outstanding ask without creating/keeping anything. Called on logout / org switch, where the
   * answer would otherwise apply to the wrong org — and where a stranded promise hangs the create forever.
   */
  reset: () => void;
  flagRemovalFailed: (name: string) => void;
  clearRemovalFailed: () => void;
}

let seq = 0;

export const useBoundaryOverlap = create<State>((set) => ({
  queue: [],
  removalFailed: null,
  // A concurrent ask (e.g. a background shp/kml parse landing while the user is mid-draw) never
  // confirmed.
  ask: (a) =>
    new Promise<boolean>((resolve) => {
      set((s) => ({ queue: [...s.queue, { ...a, id: ++seq, resolve }] }));
    }),
  answer: (keep) =>
    set((s) => {
      const [head, ...rest] = s.queue;
      head?.resolve(keep);
      return { queue: rest };
    }),
  flagRemovalFailed: (name) => set({ removalFailed: name }),
  clearRemovalFailed: () => set({ removalFailed: null }),
  // `false` = don't keep. For a pre-create ask that aborts (nothing was written); for an already-created one the
  // caller removes it. Never silently "keep" — an unanswered question must not become a yes.
  reset: () =>
    set((s) => {
      for (const e of s.queue) e.resolve(false);
      return { queue: [] };
    }),
}));
